import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BasicButton from "../../common/components/BasicButton";
import Input from "../../common/components/Input";
import Checkbox from "../../common/components/Checkbox";
import { formatDate } from "../../../utilities/format";
import { Quiz } from "../container/QuizManagementContainer";
import { Bars3Icon, MinusIcon, PlusIcon, ChevronLeftIcon, CloudArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DifficultyData {
  id: string;
  code: string;
  label: string;
  points: number;
  order: number;
}

interface QuizDetailProps {
  quizData: {
    quiz: Quiz;
    question: string;
    hint?: string;
    explanation: string;
    document: string;
    generationJobId?: string;
    choices?: Array<{
      id: string;
      label: string;
      text: string;
      isCorrect: boolean;
    }>;
    shortAnswerSpec?: {
      acceptableAnswers: string[];
      typoTolerance: number;
    };
  };
  onSaveQuiz?: (quiz: Quiz, difficultyId?: string, awardedPoints?: number) => void;
  onDownloadDocument?: () => void;
  difficulties?: DifficultyData[];
  currentDifficultyId?: string;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onReject?: () => void;
  onDelete?: () => void;
}

interface QuizDetailData extends Quiz {
  question: string;
  options: QuizOption[];
  oxAnswer?: "O" | "X";
  shortAnswers?: ShortAnswer[];
  explanation: string;
  document: string;
}

interface ShortAnswer {
  id: string;
  content: string;
}

interface QuizOption {
  id: string;
  content: string;
  isCorrect: boolean;
}

interface SortableItemProps {
  option: QuizOption;
  index: number;
  isEditMode: boolean;
  onOptionChange: (index: number, value: string) => void;
  onCorrectChange: (index: number, isCorrect: boolean) => void;
  onRemoveOption: (index: number) => void;
  canRemove: boolean;
}

function SortableItem({
  option,
  index,
  isEditMode,
  onOptionChange,
  onCorrectChange,
  onRemoveOption,
  canRemove,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center bg-white">
      {/* 문제번호/드래그 핸들 */}
      <div className="w-16 flex items-center justify-center py-2 border-r border-gray-200">
        {isEditMode ? (
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <Bars3Icon className="h-5 w-5 text-gray-400" />
          </div>
        ) : (
          <span className="text-sm font-medium text-gray-700">{index + 1}</span>
        )}
      </div>

      {/* 정답여부 체크박스 */}
      <div className="w-20 flex items-center justify-center py-2 border-r border-gray-200">
        <div
          className={`w-4 h-4 rounded-sm border transition-colors ${
            option.isCorrect
              ? "bg-blue-600 border-blue-600"
              : "border-gray-300 bg-white"
          } ${isEditMode ? "cursor-pointer" : "cursor-default"}`}
          onClick={isEditMode ? () => onCorrectChange(index, !option.isCorrect) : undefined}
        >
          {option.isCorrect && (
            <svg className="w-full h-full text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>

      {/* 보기 내용 */}
      <div className="flex-1 py-2 px-4 border-r border-gray-200">
        {isEditMode ? (
          <input
            type="text"
            value={option.content}
            onChange={(e) => onOptionChange(index, e.target.value)}
            className="w-full px-0 py-1 border-0 focus:outline-none focus:ring-0 bg-transparent"
            placeholder="보기 내용을 입력하세요"
          />
        ) : (
          <span className="text-base text-gray-900">{option.content}</span>
        )}
      </div>

      {/* 삭제 버튼 (편집 모드에서만) */}
      {isEditMode && (
        <div className="w-16 flex items-center justify-center py-2">
          <button
            onClick={() => onRemoveOption(index)}
            disabled={!canRemove}
            className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
              !canRemove
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-red-500 hover:bg-red-600"
            }`}
          >
            <MinusIcon className="h-4 w-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function QuizDetail({ quizData, onSaveQuiz, onDownloadDocument, difficulties = [], currentDifficultyId, onActivate, onDeactivate, onReject, onDelete }: QuizDetailProps) {
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { quiz } = quizData;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // API 데이터를 컴포넌트 형식으로 변환
  const getInitialQuizData = (): QuizDetailData => {
    const baseData = {
      ...quiz,
      document: quizData.document
    };

    if (quiz.type === "객관식" || quiz.type === "O/X") {
      const options = quizData.choices?.map((choice) => ({
        id: choice.id,
        content: choice.text,
        isCorrect: choice.isCorrect,
      })) || [];

      if (quiz.type === "O/X") {
        const correctChoice = options.find(opt => opt.isCorrect);
        return {
          ...baseData,
          question: quizData.question,
          options: [],
          oxAnswer: correctChoice?.content === "O" ? "O" : "X",
          explanation: quizData.explanation,
        };
      }

      return {
        ...baseData,
        question: quizData.question,
        options,
        explanation: quizData.explanation,
      };
    } else {
      // 단답형
      const shortAnswers = quizData.shortAnswerSpec?.acceptableAnswers.map((answer, index) => ({
        id: (index + 1).toString(),
        content: answer,
      })) || [];

      return {
        ...baseData,
        question: quizData.question,
        options: [],
        shortAnswers,
        explanation: quizData.explanation,
      };
    }
  };

  const [internalQuizData, setInternalQuizData] = useState<QuizDetailData>(getInitialQuizData());

  const [editableFields, setEditableFields] = useState({
    title: internalQuizData.title,
    difficulty: internalQuizData.difficulty,
    question: internalQuizData.question,
    options: [...internalQuizData.options],
    oxAnswer: internalQuizData.oxAnswer || "O",
    shortAnswers: internalQuizData.shortAnswers ? [...internalQuizData.shortAnswers] : [],
    explanation: internalQuizData.explanation,
    hint: quizData.hint || ""
  });

  const [status, setStatus] = useState<"활성" | "비활성" | "신규" | "반려">(quiz.status);

  // quiz 데이터가 변경되면 status 업데이트
  React.useEffect(() => {
    setStatus(quiz.status);
  }, [quiz.status]);

  // 현재 난이도가 활성화된 난이도 목록에 있는지 확인
  const isCurrentDifficultyActive = difficulties.some(d => d.id === currentDifficultyId);
  const [selectedDifficultyId, setSelectedDifficultyId] = useState<string>(
    isCurrentDifficultyActive ? (currentDifficultyId || "") : ""
  );
  const [awardedPoints, setAwardedPoints] = useState<number>(quiz.points);

  // 현재 선택된 난이도 정보 가져오기
  const getCurrentDifficultyData = () => {
    return difficulties.find(d => d.id === selectedDifficultyId);
  };

  // 난이도 선택 시 포인트 업데이트
  const handleDifficultySelect = (difficultyId: string) => {
    setSelectedDifficultyId(difficultyId);
    const selectedDifficulty = difficulties.find(d => d.id === difficultyId);
    if (selectedDifficulty) {
      setAwardedPoints(selectedDifficulty.points);
    }
  };

  const hasChanges =
    editableFields.title !== internalQuizData.title ||
    editableFields.difficulty !== internalQuizData.difficulty ||
    editableFields.question !== internalQuizData.question ||
    JSON.stringify(editableFields.options) !== JSON.stringify(internalQuizData.options) ||
    editableFields.oxAnswer !== internalQuizData.oxAnswer ||
    JSON.stringify(editableFields.shortAnswers) !== JSON.stringify(internalQuizData.shortAnswers) ||
    editableFields.explanation !== internalQuizData.explanation ||
    editableFields.hint !== (quizData.hint || "") ||
    status !== quiz.status;

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleSave = () => {
    // 수정사항이 없으면 바로 조회모드로
    if (!hasChanges) {
      setIsEditMode(false);
      return;
    }

    // 질문 검증
    if (!editableFields.question.trim()) {
      alert("질문을 입력해주세요.");
      return;
    }

    // 보기 검증 (객관식인 경우)
    if (internalQuizData.type === "객관식") {
      const hasEmptyOption = editableFields.options.some(option => !option.content.trim());
      if (hasEmptyOption) {
        alert("보기 내용을 입력해주세요.");
        return;
      }

      // 정답 체크 검증
      const hasCorrectAnswer = editableFields.options.some(option => option.isCorrect);
      if (!hasCorrectAnswer) {
        alert("정답을 체크해주세요.");
        return;
      }
    }

    // 정답 검증 (단답형인 경우)
    if (internalQuizData.type === "단답형") {
      if (editableFields.shortAnswers.length === 0) {
        alert("정답을 최소 1개 이상 입력해주세요.");
        return;
      }
      const hasEmptyAnswer = editableFields.shortAnswers.some(answer => !answer.content.trim());
      if (hasEmptyAnswer) {
        alert("정답 내용을 입력해주세요.");
        return;
      }
    }

    // 해설 검증
    if (!editableFields.explanation.trim()) {
      alert("해설을 입력해주세요.");
      return;
    }

    // 저장 확인
    if (!window.confirm("저장하시겠습니까?")) {
      return;
    }

    const updatedQuiz = {
      ...internalQuizData,
      ...editableFields
    };
    setInternalQuizData(updatedQuiz);
    onSaveQuiz?.(updatedQuiz, selectedDifficultyId, awardedPoints);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    if (!hasChanges) {
      // 수정된 내용이 없으면 바로 조회모드로
      setIsEditMode(false);
    } else {
      // 수정된 내용이 있으면 확인 후 조회모드로
      if (window.confirm("모든 수정사항을 취소하고 나가시겠습니까?")) {
        // 수정사항 초기화
        setEditableFields({
          title: internalQuizData.title,
          difficulty: internalQuizData.difficulty,
          question: internalQuizData.question,
          options: [...internalQuizData.options],
          oxAnswer: internalQuizData.oxAnswer || "O",
          shortAnswers: internalQuizData.shortAnswers ? [...internalQuizData.shortAnswers] : [],
          explanation: internalQuizData.explanation,
          hint: quizData.hint || ""
        });
        setStatus(quiz.status);
        // 난이도와 포인트 초기화
        const isCurrentDifficultyActive = difficulties.some(d => d.id === currentDifficultyId);
        setSelectedDifficultyId(isCurrentDifficultyActive ? (currentDifficultyId || "") : "");
        setAwardedPoints(quiz.points);
        setIsEditMode(false);
      }
    }
  };

  const handleReset = () => {
    setEditableFields({
      title: internalQuizData.title,
      difficulty: internalQuizData.difficulty,
      question: internalQuizData.question,
      options: [...internalQuizData.options],
      oxAnswer: internalQuizData.oxAnswer || "O",
      shortAnswers: internalQuizData.shortAnswers ? [...internalQuizData.shortAnswers] : [],
      explanation: internalQuizData.explanation,
      hint: quizData.hint || ""
    });
    setStatus(quiz.status);
    // 난이도와 포인트 초기화
    const isCurrentDifficultyActive = difficulties.some(d => d.id === currentDifficultyId);
    setSelectedDifficultyId(isCurrentDifficultyActive ? (currentDifficultyId || "") : "");
    setAwardedPoints(quiz.points);
  };

  const handleGoToList = () => {
    navigate("/quiz/management");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDelete = () => {
    onDelete?.();
  };

  const getStatusColor = (statusValue: "활성" | "비활성" | "신규" | "반려") => {
    switch (statusValue) {
      case "활성":
        return "bg-green-100 text-green-600 border-green-500";
      case "비활성":
        return "bg-red-100 text-red-500 border-red-300";
      case "신규":
        return "bg-yellow-200 text-yellow-700 border-yellow-600";
      case "반려":
        return "bg-gray-100 text-gray-700 border-gray-400";
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...editableFields.options];
    newOptions[index] = { ...newOptions[index], content: value };
    setEditableFields(prev => ({ ...prev, options: newOptions }));
  };

  const handleCorrectChange = (index: number, isCorrect: boolean) => {
    const newOptions = editableFields.options.map((option, i) => ({
      ...option,
      isCorrect: i === index ? isCorrect : false
    }));
    setEditableFields(prev => ({ ...prev, options: newOptions }));
  };

  const handleAddOption = () => {
    if (editableFields.options.length < 5) {
      const newOption = {
        id: Date.now().toString(),
        content: "",
        isCorrect: false
      };
      setEditableFields(prev => ({
        ...prev,
        options: [...prev.options, newOption]
      }));
    }
  };

  const handleRemoveOption = (index: number) => {
    if (editableFields.options.length > 2) {
      const newOptions = editableFields.options.filter((_, i) => i !== index);
      setEditableFields(prev => ({ ...prev, options: newOptions }));
    }
  };

  const handleShortAnswerChange = (index: number, value: string) => {
    const newAnswers = [...editableFields.shortAnswers];
    newAnswers[index] = { ...newAnswers[index], content: value };
    setEditableFields(prev => ({ ...prev, shortAnswers: newAnswers }));
  };

  const handleAddShortAnswer = () => {
    const newAnswer = {
      id: Date.now().toString(),
      content: ""
    };
    setEditableFields(prev => ({
      ...prev,
      shortAnswers: [...prev.shortAnswers, newAnswer]
    }));
  };

  const handleRemoveShortAnswer = (index: number) => {
    if (editableFields.shortAnswers.length > 1) {
      const newAnswers = editableFields.shortAnswers.filter((_, i) => i !== index);
      setEditableFields(prev => ({ ...prev, shortAnswers: newAnswers }));
    }
  };

  const handleDownload = () => {
    onDownloadDocument?.();
  };

  const handleFileSelect = (file: File) => {
    if (file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      alert("PDF 파일만 업로드 가능합니다.");
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = editableFields.options.findIndex(option => option.id === active.id);
      const newIndex = editableFields.options.findIndex(option => option.id === over.id);

      const newOptions = arrayMove(editableFields.options, oldIndex, newIndex);
      setEditableFields(prev => ({ ...prev, options: newOptions }));
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col" style={{ minWidth: '800px', height: 'calc(100vh - 120px)' }}>
      {/* 헤더 영역 - 고정 */}
      <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          {!isEditMode && (
            <button
              onClick={handleGoBack}
              className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
            </button>
          )}
          <h1 className="text-2xl font-bold">퀴즈 상세</h1>
        </div>
        {!isEditMode && (
          <div className="relative">
            <select
              value={status}
              onChange={async (e) => {
                const newStatus = e.target.value as "활성" | "비활성" | "신규" | "반려";
                const currentStatus = status;

                // 상태 변경 확인
                if (!window.confirm(`퀴즈 상태를 "${newStatus}"(으)로 변경하시겠습니까?`)) {
                  // 취소하면 원래 값으로 되돌림
                  e.target.value = currentStatus;
                  return;
                }

                // 상태에 따라 적절한 mutation 실행
                try {
                  if (newStatus === "활성") {
                    await onActivate?.();
                  } else if (newStatus === "비활성") {
                    await onDeactivate?.();
                  } else if (newStatus === "신규" || newStatus === "반려") {
                    await onReject?.();
                  }
                } catch (error) {
                  // 에러 발생 시 원래 값으로 되돌림
                  e.target.value = currentStatus;
                }
              }}
              className={`px-4 py-2 rounded-full border font-medium focus:outline-none focus:ring-0 cursor-pointer appearance-none pr-10 text-left ${getStatusColor(status)}`}
            >
              <option value="활성">활성</option>
              <option value="비활성">비활성</option>
              {status === "신규" && <option value="신규">신규</option>}
              <option value="반려">반려</option>
            </select>
            <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
              status === "활성" ? "text-green-600" :
              status === "비활성" ? "text-red-500" :
              status === "신규" ? "text-yellow-600" :
              "text-gray-700"
            }`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* 콘텐츠 영역 - 스크롤 가능 */}
      <div className="flex-1 overflow-y-auto px-6">
        <div className="space-y-8">
        {/* 기본 정보 섹션 - 조회 모드에서만 표시 */}
        {!isEditMode && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">기본 정보</h2>
            <div className="space-y-6">
              {/* 유형 */}
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  유형
                </div>
                <div className="text-sm text-gray-900 flex-1 ml-4">
                  {internalQuizData.type}
                </div>
              </div>

              {/* 생성 번호 */}
              {quizData.generationJobId && (
                <div className="flex items-center w-full">
                  <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                    생성 번호
                  </div>
                  <div className="text-sm text-gray-900 flex-1 ml-4">
                    {quizData.generationJobId}
                  </div>
                </div>
              )}

              {/* 최종수정자 */}
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  최종수정자
                </div>
                <div className="text-sm text-gray-900 flex-1 ml-4">
                  {internalQuizData.lastModifier}
                </div>
              </div>

              {/* 생성일시 */}
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  생성일시
                </div>
                <div className="text-sm text-gray-900 flex-1 ml-4">
                  {formatDate(internalQuizData.createdAt, "YYYY.MM.DD HH:mm")}
                </div>
              </div>

              {/* 수정일시 */}
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  수정일시
                </div>
                <div className="text-sm text-gray-900 flex-1 ml-4">
                  {formatDate(internalQuizData.updatedAt, "YYYY.MM.DD HH:mm")}
                </div>
              </div>

              {/* 기반자료 */}
              <div className="flex items-center w-full">
                <div className="text-sm font-medium text-gray-700 w-24 sm:w-32 lg:w-40 flex-shrink-0">
                  기반자료
                </div>
                <div className="flex items-center ml-4">
                  <span className="text-sm text-gray-900 break-all">
                    {internalQuizData.document}
                  </span>
                  <BasicButton
                    text="다운로드"
                    variant="text"
                    className="text-sm py-1 px-3 underline flex-shrink-0 ml-2 text-gray-600 hover:text-gray-800"
                    onClick={handleDownload}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 기반자료 섹션 - 수정 모드이고 generationJobId가 없을 때만 표시 */}
        {isEditMode && !quizData.generationJobId && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">기반자료 (선택)</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileInputChange}
              className="hidden"
            />

            {!selectedFile ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleUploadClick}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragOver
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                }`}
              >
                <CloudArrowUpIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-sm text-gray-600 mb-2">
                  클릭하거나 파일을 드래그하여 업로드하세요
                </p>
                <p className="text-xs text-gray-500">PDF 파일만 업로드 가능합니다</p>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                    <span className="text-red-600 text-xs font-bold">PDF</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* 난이도 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">난이도</h2>
          {isEditMode ? (
            /* 수정 모드 */
            <div className="space-y-6">
              <div className="flex gap-4">
                {difficulties.map((difficulty) => (
                  <div
                    key={difficulty.id}
                    onClick={() => handleDifficultySelect(difficulty.id)}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors flex-1 ${
                      selectedDifficultyId === difficulty.id
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="w-5 h-5 rounded border-2 border-blue-600 bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {difficulty.order}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {difficulty.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* 조회 모드 */
            <div className="flex items-center w-full">
              <div className="text-base text-gray-900 flex-1">
                {getCurrentDifficultyData()?.label || quiz.difficulty} | 획득 포인트 {quiz.points}
              </div>
            </div>
          )}
        </div>

        {/* 획득 포인트 섹션 - 수정 모드에서만 표시 */}
        {isEditMode && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">획득 포인트</h2>
            <input
              type="number"
              value={awardedPoints}
              onChange={(e) => setAwardedPoints(Number(e.target.value))}
              className="w-40 h-[40px] px-3 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="0"
              placeholder="포인트"
            />
          </div>
        )}

        {/* 질문 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">질문</h2>
          <div className="space-y-3">
            {isEditMode ? (
              <textarea
                value={editableFields.question}
                onChange={(e) => setEditableFields(prev => ({ ...prev, question: e.target.value }))}
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="질문을 입력하세요"
              />
            ) : (
              <div className="w-full h-24 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 overflow-y-auto">
                <div className="text-base text-gray-900 whitespace-pre-wrap">
                  {internalQuizData.question}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 보기 섹션 - 객관식일 때만 표시 */}
        {internalQuizData.type === "객관식" && (
          <div>
            <h2 className="text-lg font-semibold mb-2 text-gray-800">보기</h2>
            {/* 안내 문구 - 수정 모드에서만 표시 */}
            {isEditMode && (
              <p className="text-xs text-gray-500 mb-4">
                보기를 2개 이상 5개 이하로 추가하고 정답을 1개 이상 체크해주세요.
              </p>
            )}
            <div className="space-y-3">
              {/* 보기 테이블 */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={editableFields.options.map(option => option.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {editableFields.options.map((option, index) => (
                      <div key={option.id} className="border-b border-gray-200 last:border-b-0">
                        <SortableItem
                          option={option}
                          index={index}
                          isEditMode={isEditMode}
                          onOptionChange={handleOptionChange}
                          onCorrectChange={handleCorrectChange}
                          onRemoveOption={handleRemoveOption}
                          canRemove={editableFields.options.length > 2}
                        />
                      </div>
                    ))}
                  </SortableContext>
                </DndContext>

                {/* 보기 추가 행 (편집 모드에서만) */}
                {isEditMode && (
                  <div className="flex justify-end py-3 px-4 bg-white">
                    <button
                      onClick={handleAddOption}
                      disabled={editableFields.options.length >= 5}
                      className={`flex items-center gap-2 px-4 py-2 text-sm rounded border transition-colors ${
                        editableFields.options.length >= 5
                          ? "text-gray-400 bg-white border-gray-300 cursor-not-allowed"
                          : "text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200"
                      }`}
                    >
                      <PlusIcon className="h-4 w-4" />
                      보기 추가
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 정답 섹션 - O/X일 때만 표시 */}
        {internalQuizData.type === "O/X" && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-gray-800">정답</h2>
            <div className="flex gap-4">
              <div
                onClick={isEditMode ? () => setEditableFields(prev => ({ ...prev, oxAnswer: "O" })) : undefined}
                className={`flex items-center justify-center p-6 rounded-lg border-2 transition-colors flex-1 ${
                  editableFields.oxAnswer === "O"
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-300 bg-white"
                } ${isEditMode ? "cursor-pointer hover:bg-gray-50" : "cursor-default"}`}
              >
                <span className={`text-2xl ${editableFields.oxAnswer === "O" ? "text-blue-600" : "text-gray-500"}`}>O</span>
              </div>
              <div
                onClick={isEditMode ? () => setEditableFields(prev => ({ ...prev, oxAnswer: "X" })) : undefined}
                className={`flex items-center justify-center p-6 rounded-lg border-2 transition-colors flex-1 ${
                  editableFields.oxAnswer === "X"
                    ? "border-blue-600 bg-blue-50"
                    : "border-gray-300 bg-white"
                } ${isEditMode ? "cursor-pointer hover:bg-gray-50" : "cursor-default"}`}
              >
                <span className={`text-2xl ${editableFields.oxAnswer === "X" ? "text-blue-600" : "text-gray-500"}`}>X</span>
              </div>
            </div>
          </div>
        )}

        {/* 정답 섹션 - 단답형일 때만 표시 */}
        {internalQuizData.type === "단답형" && (
          <div>
            <h2 className="text-lg font-semibold mb-2 text-gray-800">정답</h2>
            {/* 안내 문구 - 수정 모드에서만 표시 */}
            {isEditMode && (
              <p className="text-xs text-gray-500 mb-4">
                정답으로 인정할 내용을 모두 입력해주세요.
              </p>
            )}
            <div className="space-y-3">
              {/* 정답 테이블 */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {editableFields.shortAnswers.map((answer, index) => (
                  <div key={answer.id} className="flex items-center bg-white border-b border-gray-200 last:border-b-0">
                    {/* 순번 */}
                    <div className="w-16 flex items-center justify-center py-2 border-r border-gray-200">
                      <span className="text-sm font-medium text-gray-700">{index + 1}</span>
                    </div>

                    {/* 정답 내용 */}
                    <div className="flex-1 py-2 px-4 border-r border-gray-200">
                      {isEditMode ? (
                        <input
                          type="text"
                          value={answer.content}
                          onChange={(e) => handleShortAnswerChange(index, e.target.value)}
                          className="w-full px-0 py-1 border-0 focus:outline-none focus:ring-0 bg-transparent"
                          placeholder="정답 내용을 입력하세요"
                        />
                      ) : (
                        <span className="text-base text-gray-900">{answer.content}</span>
                      )}
                    </div>

                    {/* 삭제 버튼 (편집 모드에서만) */}
                    {isEditMode && (
                      <div className="w-16 flex items-center justify-center py-2">
                        <button
                          onClick={() => handleRemoveShortAnswer(index)}
                          disabled={editableFields.shortAnswers.length <= 1}
                          className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                            editableFields.shortAnswers.length <= 1
                              ? "bg-gray-300 cursor-not-allowed"
                              : "bg-red-500 hover:bg-red-600"
                          }`}
                        >
                          <MinusIcon className="h-4 w-4 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* 정답 추가 행 (편집 모드에서만) */}
                {isEditMode && (
                  <div className="flex justify-end py-3 px-4 bg-white">
                    <button
                      onClick={handleAddShortAnswer}
                      className="flex items-center gap-2 px-4 py-2 text-sm rounded border transition-colors text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200"
                    >
                      <PlusIcon className="h-4 w-4" />
                      정답 추가
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 해설 섹션 */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-gray-800">해설</h2>
          <div className="space-y-3">
            {isEditMode ? (
              <textarea
                value={editableFields.explanation}
                onChange={(e) => setEditableFields(prev => ({ ...prev, explanation: e.target.value }))}
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="해설을 입력하세요"
              />
            ) : (
              <div className="w-full h-32 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 overflow-y-auto">
                <div className="text-base text-gray-900 whitespace-pre-wrap">
                  {internalQuizData.explanation}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 힌트 섹션 */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">힌트 (선택)</h2>
          <div className="space-y-3">
            {isEditMode ? (
              <textarea
                value={editableFields.hint}
                onChange={(e) => setEditableFields(prev => ({ ...prev, hint: e.target.value }))}
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="힌트를 입력하세요 (선택)"
              />
            ) : (
              <div className="w-full h-24 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 overflow-y-auto">
                <div className="text-base text-gray-900 whitespace-pre-wrap">
                  {editableFields.hint || "-"}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* 버튼 영역 - 고정 */}
      <div className="p-6 pt-4 border-t border-gray-200 flex-shrink-0">
        {isEditMode ? (
          <div className="flex justify-between items-center">
            <BasicButton
              text="초기화"
              variant="outlined"
              className="px-6 py-2 border-gray-400 text-gray-700 hover:bg-gray-100"
              onClick={handleReset}
            />
            <div className="flex gap-3">
              <BasicButton
                text="취소"
                variant="outlined"
                className="px-6 py-2 border-black text-black hover:bg-gray-100"
                onClick={handleCancel}
              />
              <BasicButton
                text="저장"
                variant="primary"
                className="px-6 py-2 bg-black text-white hover:bg-gray-800"
                onClick={handleSave}
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <BasicButton
              text="삭제"
              variant="outlined"
              className="px-6 py-2 border-red-500 text-red-500 hover:bg-red-50"
              onClick={handleDelete}
            />
            <div className="flex gap-3">
              <BasicButton
                text="수정하기"
                variant="outlined"
                className="px-6 py-2 border-black text-black hover:bg-gray-100"
                onClick={handleEditMode}
              />
              <BasicButton
                text="목록으로"
                variant="primary"
                className="px-6 py-2 bg-black text-white hover:bg-gray-800"
                onClick={handleGoToList}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}